/**
 * User Identity Layer
 *
 * When signed in with Google (Supabase), the auth user id is used as owner so profiles
 * follow the account across devices. When signed out, a local UUID is used (device-only).
 */

import { createClient } from "@/lib/supabase/client";

const USER_ID_KEY = "Mindaloo_user_id";
const USER_CREATED_KEY = "Mindaloo_user_created";

/** Set by UserProvider when Supabase session is loaded. When set, getUserId() returns this so profiles use the Google account. */
let cachedAuthUserId: string | null = null;

export function setCachedAuthUserId(userId: string | null): void {
  cachedAuthUserId = userId;
}

export function getCachedAuthUserId(): string | null {
  return cachedAuthUserId;
}

/** Device-only id from localStorage, if any (does not create a new id). Used to migrate AIs after Google sign-in. */
export function getStoredDeviceUserId(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return localStorage.getItem(USER_ID_KEY);
  } catch {
    return null;
  }
}

/**
 * Owner id for IndexedDB / profile writes. Prefer Supabase auth user when signed in.
 * Call this from async save paths so AIs are not saved under the anonymous device UUID
 * when the auth session exists but the React cache has not been filled yet (race on load).
 */
export async function resolveOwnerUserId(): Promise<string> {
  if (typeof window === "undefined") return "server-placeholder";
  const cached = getCachedAuthUserId();
  if (cached) return cached;
  try {
    const supabase = createClient();
    const { data: { user }, error } = await supabase.auth.getUser();
    if (!error && user?.id) {
      setCachedAuthUserId(user.id);
      return user.id;
    }
  } catch {
    // ignore
  }
  return getUserId();
}

export interface UserIdentity {
  id: string;
  createdAt: number;
  subscriptionTier?: "free" | "personal" | "pro" | "business" | "enterprise";
  email?: string;
  isSupabaseUser?: boolean;
}

/**
 * Get current user id for ownership (profiles, etc.).
 * When signed in with Supabase (e.g. Google), returns the auth user id so data follows the account.
 * When signed out, returns a local UUID (device-only).
 */
export function getUserId(): string {
  if (typeof window === "undefined") {
    return "server-placeholder";
  }
  if (cachedAuthUserId) {
    return cachedAuthUserId;
  }

  try {
    let userId = localStorage.getItem(USER_ID_KEY);

    if (!userId) {
      userId = crypto.randomUUID();
      localStorage.setItem(USER_ID_KEY, userId);
      localStorage.setItem(USER_CREATED_KEY, Date.now().toString());

      if (typeof process !== "undefined" && process.env.NODE_ENV === "development") {
        console.log("🆔 New user identity created:", userId);
      }
    }

    return userId;
  } catch {
    return "anonymous";
  }
}

/**
 * Get full user identity with metadata
 */
export function getUserIdentity(): UserIdentity {
  const id = getUserId();
  try {
    const createdStr = typeof window !== "undefined" ? localStorage.getItem(USER_CREATED_KEY) : null;
    const createdAt = createdStr ? parseInt(createdStr, 10) : Date.now();
    return { id, createdAt };
  } catch {
    return { id, createdAt: Date.now() };
  }
}

/**
 * Check if user identity exists
 */
export function hasUserIdentity(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return !!localStorage.getItem(USER_ID_KEY);
  } catch {
    return false;
  }
}

/**
 * Clear user identity (for testing/logout)
 * Also clears the Supabase auth cache so getUserId() falls back to local.
 */
export function clearUserIdentity(): void {
  if (typeof window === "undefined") return;
  setCachedAuthUserId(null);
  try {
    localStorage.removeItem(USER_ID_KEY);
    localStorage.removeItem(USER_CREATED_KEY);
    console.warn("⚠️ User identity cleared. All data is now orphaned.");
  } catch {
    // ignore
  }
}

